import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, QrCode, AddressBook } from "@phosphor-icons/react";
import AppHeader from "@/components/AppHeader";
import api, { apiErrorMessage } from "@/lib/api";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PropertiesPane } from "@/components/service/PropertiesPane";
import { RecordsPane } from "@/components/service/RecordsPane";
import { DetailPane } from "@/components/service/DetailPane";
import { PropertyModal } from "@/components/service/PropertyModal";
import { RecordModal } from "@/components/service/RecordModal";
import { PhotoViewer } from "@/components/service/PhotoViewer";
import { VideoViewer } from "@/components/service/VideoViewer";
import { LiveSearchBar } from "@/components/service/LiveSearchBar";
import { useLiveSearch } from "@/components/service/useLiveSearch";
import { QrCodeModal } from "@/components/service/QrCodeModal";
import { authClient } from "@/components/service/serviceApi";
import { Button } from "@/components/ui/button";
import { useUploadQueue } from "@/components/service/useUploadQueue";
import { useRecurring } from "@/components/service/recurring/useRecurring";
import { ContactsModal } from "@/components/service/recurring/ContactsModal";
import { ScheduleDialog } from "@/components/service/recurring/ScheduleDialog";

const useIsDesktop = () => {
  const [d, setD] = useState(() => window.innerWidth >= 1024);
  useEffect(() => { const h = () => setD(window.innerWidth >= 1024); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return d;
};

const scrollToTestId = (id) => setTimeout(() => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 150);

export default function ServicePage() {
  const [params, setParams] = useSearchParams();
  const propertyId = params.get("property") || "";
  const recordId = params.get("record") || "";
  const isDesktop = useIsDesktop();
  const [mobileTab, setMobileTab] = useState(recordId ? "detail" : propertyId ? "records" : "properties");

  const [access, setAccess] = useState({ can_delete: false });
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [records, setRecords] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState({ props: true, records: false, detail: false });
  const [propModal, setPropModal] = useState({ open: false, property: null });
  const [recModal, setRecModal] = useState({ open: false, record: null });
  const [viewer, setViewer] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const queue = useUploadQueue();
  const [qrOpen, setQrOpen] = useState(false);
  const search = useLiveSearch(authClient);
  const rec = useRecurring(propertyId);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [schedDlg, setSchedDlg] = useState({ open: false, schedule: null, record: null });
  const openSchedule = (s) => setSchedDlg({ open: true, schedule: s, record: null });
  const [selSchedId, setSelSchedId] = useState(null);
  const pickSchedule = (s) => { setSelSchedId(s.id); select(propertyId, s.record_id); setMobileTab("detail"); };
  const detailSchedule = detail ? rec.schedules.find((s) => s.status !== "inactive" && (s.record_id === detail.record.id || s.id === detail.record.recurring_schedule_id)) : null;
  const prevSel = useRef(null);

  const select = useCallback((p, r) => {
    const next = {};
    if (p) next.property = p;
    if (r) next.record = r;
    setParams(next, { replace: false });
  }, [setParams]);

  const loadProperties = useCallback(async () => {
    try { const { data } = await api.get("/service/properties"); setProperties(data); } catch (e) { toast.error(apiErrorMessage(e, "Could not load properties")); }
    setLoading((l) => ({ ...l, props: false }));
  }, []);
  const loadRecords = useCallback(async () => {
    if (!propertyId) { setRecords([]); return; }
    setLoading((l) => ({ ...l, records: true }));
    try { const { data } = await api.get("/service/records", { params: { property_id: propertyId } }); setRecords(data); } catch (e) { toast.error(apiErrorMessage(e, "Could not load records")); }
    setLoading((l) => ({ ...l, records: false }));
  }, [propertyId]);
  const loadDetail = useCallback(async (quiet) => {
    if (!recordId) { setDetail(null); return; }
    if (!quiet) setLoading((l) => ({ ...l, detail: true }));
    try { const { data } = await api.get(`/service/records/${recordId}`); setDetail(data); } catch (e) { setDetail(null); toast.error(apiErrorMessage(e, "Could not load record")); }
    setLoading((l) => ({ ...l, detail: false }));
  }, [recordId]);

  useEffect(() => {
    loadProperties();
    api.get("/service/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/service/access").then(({ data }) => setAccess(data)).catch(() => {});
  }, [loadProperties]);
  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadDetail(); queue.reset(); }, [loadDetail]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (propertyId) scrollToTestId(`service-property-${propertyId}`); }, [propertyId, properties.length]);
  useEffect(() => { if (recordId) scrollToTestId(`service-records-table-row-${recordId}`); }, [recordId, records.length]);

  const processing = detail?.media?.some((m) => m.processing_status === "processing");
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(() => loadDetail(true), 4000);
    return () => clearInterval(t);
  }, [processing, loadDetail]);

  const property = properties.find((p) => p.id === propertyId) || null;
  const visibleProps = search.active ? properties.filter((p) => search.result.property_ids.includes(p.id)) : properties;
  const visibleRecords = records.filter(search.isVisibleRecord);

  const onSearchChange = (v) => {
    if (!search.q.trim() && v.trim()) prevSel.current = { property: propertyId, record: recordId };
    if (search.q.trim() && !v.trim() && prevSel.current) { select(prevSel.current.property, prevSel.current.record); prevSel.current = null; }
    search.setQ(v);
  };

  // Never show stale selections that the live filter excludes.
  useEffect(() => {
    if (!search.active || search.loading) return;
    if (propertyId && !search.result.property_ids.includes(propertyId)) { select(); return; }
    if (recordId && !loading.records && records.length && !visibleRecords.some((r) => r.id === recordId)) select(propertyId);
  }, [search.active, search.loading, search.result, propertyId, recordId, records, loading.records]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAll = () => { loadProperties(); loadRecords(); loadDetail(true); };

  const onMediaChanged = useCallback((m) => {
    setDetail((d) => (d ? { ...d, media: d.media.map((x) => (x.id === m.id ? { ...x, ...m } : x)) } : d));
    setViewer((v) => (v && v.id === m.id ? { ...v, ...m } : v));
  }, []);

  const uploadNow = async (files) => {
    if (!detail) return;
    const failed = await queue.addAndRun(authClient, detail.record.id, files);
    if (failed) toast.error(`${failed} upload(s) failed — use retry`);
    else toast.success(`${files.length} file(s) uploaded`);
    loadDetail(true); loadRecords();
  };

  const askDelete = (kind, item) => {
    const cfg = {
      property: { title: `Delete ${item.street}?`, desc: "The address will be removed. Addresses with service records cannot be deleted.", run: () => api.delete(`/service/properties/${item.id}`), after: () => { select(); loadProperties(); } },
      record: { title: `Delete “${item.title}”?`, desc: "This removes the service entry and its photos and videos from Strata.", run: () => api.delete(`/service/records/${item.id}`), after: () => { select(propertyId); refreshAll(); } },
      media: { title: `Delete ${item.display_name || item.filename}?`, desc: `This ${item.type || "attachment"} will be permanently removed from the service entry. The deletion is logged.`, run: () => api.delete(`/service/media/${item.id}`), after: () => { setViewer(null); loadDetail(true); loadRecords(); } },
    }[kind];
    setConfirm(cfg);
  };
  const runConfirm = async () => {
    const c = confirm; setConfirm(null);
    try { await c.run(); toast.success("Deleted"); c.after(); } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const panes = {
    properties: (
      <PropertiesPane
        properties={visibleProps}
        searchActive={search.active}
        selectedId={propertyId}
        loading={loading.props}
        canDelete={access.can_delete}
        onSelect={(p) => { select(p.id); setMobileTab("records"); }}
        onAdd={() => setPropModal({ open: true, property: null })}
        onEdit={(p) => setPropModal({ open: true, property: p })}
        onDelete={(p) => askDelete("property", p)}
      />
    ),
    records: (
      <RecordsPane
        property={property}
        records={visibleRecords}
        searchActive={search.active}
        selectedId={recordId}
        loading={loading.records}
        onSelect={(r) => { setSelSchedId(null); select(propertyId, r.id); setMobileTab("detail"); }}
        onNew={() => setRecModal({ open: true, record: null })}
        schedules={search.active ? [] : rec.schedules}
        onOpenSchedule={pickSchedule}
        selectedScheduleId={selSchedId}
        overdue={rec.status.overdue}
      />
    ),
    detail: (
      <DetailPane
        detail={detail}
        loading={loading.detail}
        canDelete={access.can_delete}
        onEdit={() => setRecModal({ open: true, record: detail.record })}
        schedule={detailSchedule}
        onOpenSchedule={openSchedule}
        canManageRecurring={rec.status.can_manage}
        onScheduleChanged={() => rec.loadSchedules()}
        onMakeRecurring={rec.status.can_manage && detail && !detail.record.recurring_schedule_id ? () => setSchedDlg({ open: true, schedule: null, record: detail.record }) : undefined}
        onDelete={() => askDelete("record", detail.record)}
        onDeleteMedia={access.can_delete_media ? (m) => askDelete("media", m) : undefined}
        onOpenMedia={setViewer}
        onOpenRelated={(r) => select(propertyId, r.id)}
        queue={queue}
        onUploadFiles={(files) => uploadNow(files)}
        onRetryUpload={async (it) => { await queue.runOne(authClient, detail.record.id, it); loadDetail(true); loadRecords(); }}
      />
    ),
  };

  return (
    <div className="h-screen bg-[#f5f7fb] flex flex-col overflow-hidden" data-testid="service-page">
      <AppHeader />
      <div className="px-3 sm:px-5 pt-3 pb-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 mr-2">
          <Wrench size={22} weight="bold" className="text-[#327de3]" />
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="service-title">Service</h1>
        </div>
        <LiveSearchBar value={search.q} onChange={onSearchChange} result={search.result} loading={search.loading}
          onPick={(res) => { prevSel.current = null; select(res.record.property_id, res.record.id); setMobileTab("detail"); }} />
        {rec.status.can_manage && (
          <Button onClick={() => setContactsOpen(true)} className="ml-auto h-10 bg-yellow-100 hover:bg-yellow-200 text-yellow-900 border border-yellow-300" data-testid="service-manage-contacts-btn">
            <AddressBook size={16} className="mr-1.5" /> Manage Recurring Contact List
          </Button>
        )}
        {access.can_manage_portal && (
          <Button onClick={() => setQrOpen(true)} className={`${rec.status.can_manage ? "" : "ml-auto "}h-10 bg-slate-900 hover:bg-slate-800 text-white`} data-testid="service-generate-qr-btn">
            <QrCode size={16} className="mr-1.5" /> Generate QR Code
          </Button>
        )}
      </div>
      {!isDesktop && (
        <div className="px-3 pb-2 flex gap-1" role="tablist" data-testid="service-mobile-tabs">
          {[["properties", "Properties"], ["records", "History"], ["detail", "Detail"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMobileTab(k)} className={`flex-1 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${mobileTab === k ? "bg-[#327de3] text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`service-mobile-tab-${k}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="flex-1 min-h-0 px-3 sm:px-5 pb-4">
        <div className="h-full rounded-xl border border-slate-200 overflow-hidden shadow-sm bg-white">
          {isDesktop ? (
            <ResizablePanelGroup direction="horizontal" autoSaveId="strata-service-panes">
              <ResizablePanel defaultSize={20} minSize={13}>{panes.properties}</ResizablePanel>
              <ResizableHandle withHandle data-testid="service-pane-handle-1" />
              <ResizablePanel defaultSize={25} minSize={18}>{panes.records}</ResizablePanel>
              <ResizableHandle withHandle data-testid="service-pane-handle-2" />
              <ResizablePanel defaultSize={55} minSize={30}>{panes.detail}</ResizablePanel>
            </ResizablePanelGroup>
          ) : panes[mobileTab]}
        </div>
      </div>

      <PropertyModal
        open={propModal.open}
        property={propModal.property}
        onOpenChange={(v) => setPropModal((s) => ({ ...s, open: v }))}
        onSaved={(p) => { loadProperties(); if (!propModal.property) select(p.id); else loadDetail(true); }}
      />
      <RecordModal
        client={authClient}
        open={recModal.open}
        record={recModal.record}
        defaultPropertyId={propertyId}
        properties={properties}
        categories={categories}
        onCategoryAdded={(c) => setCategories((list) => (list.some((x) => x.id === c.id) ? list : [...list, c]))}
        onOpenChange={(v) => setRecModal((s) => ({ ...s, open: v }))}
        onSaved={(r) => { select(r.property_id, r.id); loadProperties(); loadRecords(); loadDetail(true); }}
        rec={rec}
        onScheduleSaved={() => rec.loadSchedules()}
      />
      <ContactsModal open={contactsOpen} onOpenChange={setContactsOpen} rec={rec} onChanged={() => { rec.loadPeople(); rec.loadSchedules(); }} />
      <ScheduleDialog
        open={schedDlg.open}
        onOpenChange={(v) => setSchedDlg((s) => ({ ...s, open: v }))}
        schedule={schedDlg.schedule ? rec.schedules.find((s) => s.id === schedDlg.schedule.id) || schedDlg.schedule : null}
        record={schedDlg.record}
        rec={rec}
        onChanged={() => { rec.loadSchedules(); loadRecords(); }}
      />
      <PhotoViewer
        media={viewer?.type === "photo" ? viewer : null}
        open={viewer?.type === "photo"}
        onOpenChange={(v) => !v && setViewer(null)}
        onChanged={onMediaChanged}
        canDelete={access.can_delete_media}
        onDelete={(m) => askDelete("media", m)}
      />
      <VideoViewer
        client={authClient}
        media={viewer?.type === "video" ? viewer : null}
        open={viewer?.type === "video"}
        onOpenChange={(v) => !v && setViewer(null)}
        recordId={detail?.record?.id}
        onMediaChanged={onMediaChanged}
        onRecordChanged={() => { loadDetail(true); loadRecords(); }}
        canDelete={access.can_delete_media}
        onDelete={(m) => askDelete("media", m)}
      />
      <QrCodeModal open={qrOpen} onOpenChange={setQrOpen} properties={properties} defaultPropertyId={propertyId} onChanged={loadProperties} />
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="service-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="service-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="service-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
